/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ReactNode } from "react";
import { Box } from "./widgets";
import { humanize } from "./model";

const toList = (v: unknown): string[] => (Array.isArray(v) ? v.filter(Boolean).map(String) : typeof v === "string" && v.trim() ? [v] : []);
const Chips = ({ items }: { items: string[] }) => <span className="rchips">{items.map((x) => <span key={x} className="rchip">{humanize(x) || x}</span>)}</span>;

type Row = [string, ReactNode | null];
const RoleBox = ({ title, sub, rows }: { title: string; sub?: string; rows: Row[] }) => {
  const shown = rows.filter(([, v]) => v != null && v !== "");
  return (
    <Box title={title} sub={sub}>
      <dl className="rl">
        {shown.length ? shown.map(([k, v]) => (<div key={k}><dt>{k}</dt><dd>{v}</dd></div>)) : <p className="acap" style={{ padding: 18 }}>No details added yet.</p>}
      </dl>
    </Box>
  );
};

/**
 * Public view of the officiating / streaming roles on a profile. Deliberately leaves out fees and pricing:
 * those are only editable (and visible) in the owner's Roles & Capabilities tab.
 */
const RoleCards = ({ player, scorer, streamer }: { player: any; scorer?: any; streamer?: any }) => {
  const isScorer = !!scorer?.active;
  const isStreamer = !!streamer?.active;
  const isUmpire = !!player?.isUmpire;
  if (!isScorer && !isUmpire && !isStreamer) return null;
  const yrs = (n: unknown) => (typeof n === "number" && n > 0 ? `${n} year${n === 1 ? "" : "s"}` : null);
  return (
    <div className="rolecards">
      {isScorer && (
        <RoleBox title="Scorer" sub={scorer.verified ? "Verified" : undefined} rows={[
          ["Experience", yrs(scorer.experienceYears)], ["City", scorer.city || null],
          ["Scoring interface", toList(scorer.scoringInterfacePreferred).length ? <Chips items={toList(scorer.scoringInterfacePreferred)} /> : null],
          ["Scoring methods", toList(scorer.familiarScoringMethods).length ? <Chips items={toList(scorer.familiarScoringMethods)} /> : null],
          ["Languages", toList(scorer.languagesKnown).length ? <Chips items={toList(scorer.languagesKnown)} /> : null],
          ["About", scorer.bio || null],
        ]} />
      )}
      {isUmpire && (
        <RoleBox title="Umpire" rows={[
          ["Experience", yrs(player.experience)],
          ["Available in", toList(player.locations).length ? <Chips items={toList(player.locations)} /> : null],
        ]} />
      )}
      {isStreamer && (
        <RoleBox title="Streamer" sub={streamer.availableForPaidStreaming ? "Available for paid streaming" : undefined} rows={[
          ["Camera setup", toList(streamer.cameraSetup).length ? <Chips items={toList(streamer.cameraSetup)} /> : null],
          ["Streaming device", toList(streamer.streamingDevice).length ? <Chips items={toList(streamer.streamingDevice)} /> : null],
          ["Audio setup", toList(streamer.audioSetup).length ? <Chips items={toList(streamer.audioSetup)} /> : null],
          ["Platforms", toList(streamer.streamingPlatforms).length ? <Chips items={toList(streamer.streamingPlatforms)} /> : null],
          ["Sample stream", streamer.sampleStreamLink ? <a className="link" href={streamer.sampleStreamLink} target="_blank" rel="noopener noreferrer">Watch a sample</a> : null],
        ]} />
      )}
    </div>
  );
};
export default RoleCards;
